import {
  AST,
  Expression,
  FluentOrderBy,
  Relation,
  RelationRef,
  TC,
  Ty,
} from '@cotera/era';
import { Sink, Schedule, StreamSpecifier } from '@cotera/api';
import { EventStreamDefinition } from '../manifest/manifest';
import _ from 'lodash';
import { SinkOutputTypeReqs } from './sink-output-types';

export type EventStreamThrottle = {
  frequency: AST.DateTruncUnit;
  priorityBy: (t: RelationRef) => FluentOrderBy;
  maxEvents: number;
};

export const EventStream = (props: {
  specifier: StreamSpecifier;
  identifier: (t: RelationRef) => string | Expression;
  rel: Relation;
  when?: Schedule;
  onChange: (t: RelationRef) => Expression | string;
  sinks: {
    [sinkName: string]: Sink;
  };
  debounceByIdForSecs?: number;
  throttle?: EventStreamThrottle;
}): EventStreamDefinition => {
  const rel = Relation.wrap(props.rel);
  const ref = rel.ref('from');

  const sinkRef = new RelationRef(
    {
      attributes: {
        ...rel.attributes,
        __event_cotera_stable_id: Ty.makeNotNullable('string'),
        __event_detected_at: Ty.makeNotNullable('timestamp'),
        __event_value: Ty.shorthandToTy('string'),
      },
    },
    'from'
  );

  const sinks = _.mapValues(props.sinks, (sink, sinkName) => {
    const select = _.mapValues(sink.select(sinkRef), (expr) =>
      Expression.wrap(expr)
    );

    const reqs = SinkOutputTypeReqs[sink.config.t];

    if (reqs) {
      const res = TC.implementsRel({
        subject: _.mapValues(select, (x) => x.ty),
        reqs,
      });

      if (res.isErr()) {
        const err = res.error
          .toStackTrace({ location: ['sink', sinkName] })
          .toError({
            jsStackPointer: EventStream,
          });

        throw err;
      }
    }

    return {
      config: sink.config,
      condition: Expression.wrap(sink.condition(sinkRef)).ast,
      select: _.mapValues(select, (x) => x.ast),
    };
  });

  return {
    specifier: props.specifier,
    identifier: Expression.wrap(props.identifier(ref)).cast('string').ast,
    rel: rel.ast,
    when: props.when ?? 'daily',
    onChange: Expression.wrap(props.onChange(ref)).ast,
    debounceByIdForSecs: props.debounceByIdForSecs,
    throttle: props.throttle,
    sinks,
  };
};
